import { Controller } from "@hotwired/stimulus"
import {
  Chart,
  BarController,
  BarElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend
} from "chart.js"

Chart.register(
  BarController,
  BarElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend
)

// Builds one stacked-column Chart.js chart from a JSON `series` value plus a
// custom avatar legend, and tears the chart down on disconnect so
// Turbo-frame reloads don't leak chart instances.
export default class extends Controller {
  static values = { series: Object }
  static targets = ["canvas", "legend"]

  connect() {
    this.chart = new Chart(this.canvasTarget, {
      type: "bar",
      data: this.seriesValue,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "nearest", intersect: false },
        scales: {
          x: { stacked: true },
          y: { stacked: true, beginAtZero: true }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            mode: "nearest",
            intersect: false,
            // Zero-value series stack onto identical coordinates, so
            // "nearest" returns all of them — keep only the closest.
            filter: (_item, index) => index === 0
          }
        }
      }
    })

    this.buildLegend()
  }

  buildLegend() {
    const legend = this.legendTarget
    legend.replaceChildren()

    this.seriesValue.datasets.forEach((dataset, i) => {
      const row = document.createElement("button")
      row.type = "button"
      row.className =
        "flex items-center gap-2 w-full px-2 py-1 text-left text-sm rounded hover:bg-gray-50"

      const avatar = document.createElement("img")
      avatar.src = dataset.avatarUrl
      avatar.alt = dataset.label
      avatar.className = "w-6 h-6 rounded-full shrink-0"

      const swatch = document.createElement("span")
      swatch.className = "w-2 h-2 rounded-full shrink-0"
      swatch.style.backgroundColor = dataset.borderColor

      const name = document.createElement("span")
      name.className = "flex-1 truncate text-gray-700"
      name.textContent = dataset.label
      name.title = dataset.label

      const total = document.createElement("span")
      total.className = "tabular-nums font-medium text-gray-900"
      total.textContent = (dataset.total ?? 0).toLocaleString()

      row.append(avatar, swatch, name)

      if (dataset.former) {
        const badge = document.createElement("span")
        badge.className =
          "shrink-0 text-xs bg-gray-100 text-gray-500 rounded px-1.5 py-0.5"
        badge.textContent = "Former"
        row.append(badge)
      }

      row.append(total)
      row.addEventListener("click", () => {
        const visible = this.chart.isDatasetVisible(i)
        this.chart.setDatasetVisibility(i, !visible)
        this.chart.update()
        row.style.opacity = visible ? "0.4" : "1"
      })

      legend.appendChild(row)
    })
  }

  disconnect() {
    this.chart?.destroy()
    this.legendTarget.replaceChildren()
  }
}
